import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { YCanvas } from '../sessions/session/wb-canvas/fabric-utils';
import { SpaceRepository } from '../state/space.repository';
import { SCREEN_SHARE_ANNOTATIONS_ID } from '../sessions/common/session-call-user/screen-share-annotations/screen-share-annotations.utils';

export enum ActiveCanvasType {
  WHITEBOARD = 'whiteboard',
  GALLERY_VIEW = 'gallery-view', // canvas for screenshare and full-screen app annotations
}

/**
 * Service to manage the active canvas state.
 */
@Injectable({
  providedIn: 'root',
})
export class ActiveCanvasStateService {
  private readonly _activeCanvasType$ = new BehaviorSubject<ActiveCanvasType>(
    ActiveCanvasType.WHITEBOARD,
  );
  private readonly _activeCanvas$ = new BehaviorSubject<YCanvas | null>(null);
  public readonly activeCanvas$ = this._activeCanvas$.asObservable();
  private readonly _activeTileId$ = new BehaviorSubject<string>('');
  public readonly activeTileId$ = this._activeTileId$.asObservable();

  constructor(private spaceRepo: SpaceRepository) {}

  /**
   * Observable of the current active canvas type.
   */
  public readonly activeCanvasType$: Observable<ActiveCanvasType> = this._activeCanvasType$;

  /**
   * Gets the current active canvas type synchronously.
   */
  public get activeCanvasType(): ActiveCanvasType {
    return this._activeCanvasType$.value;
  }

  /**
   * Sets the active canvas type.
   *
   * @param canvasType - The canvas type to set as active ('whiteboard' or 'screenshare')
   */
  public setActiveCanvasType(canvasType: ActiveCanvasType): void {
    this._activeCanvasType$.next(canvasType);
  }

  public setActiveCanvas(canvas: YCanvas | null): void {
    this._activeCanvas$.next(canvas);
  }

  public get activeCanvas(): YCanvas | null {
    return this._activeCanvas$.getValue();
  }

  set activeTileId(tileId: string) {
    this._activeTileId$.next(tileId);
  }

  get activeTileId(): string {
    return this._activeTileId$.value;
  }

  public getScreenShareAnnotationsRoomId(): string {
    return `${this.spaceRepo.activeSpaceId}-${SCREEN_SHARE_ANNOTATIONS_ID}`;
  }
}
